// Single source of truth for how a checklist document is flattened into a
// tabular row. Used by the CSV export, so
// both always produce identical columns in the same order. Covers the REVISED
// form (3-state marks); rows submitted before the
// revision produce blanks in the per-item columns.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { NEW_CHECKLIST_SECTIONS, extraKindFor, isExpiryDay, } from "./checklistFields.js";
import { isV1Checklist } from "./checklistFieldsV1.js";
// Format in Saudi Arabia time (Asia/Riyadh = UTC+3, no DST). The sheet syncs
// run server-side in Convex (UTC), so we can't rely on the runtime's local
// zone — shift the epoch by +3h and read UTC parts to get Riyadh wall-clock.
const RIYADH_OFFSET_MS = 3 * 60 * 60 * 1000;
export function fmtDateTime(ms) {
    if (typeof ms !== "number")
        return "";
    const d = new Date(ms + RIYADH_OFFSET_MS);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
// "Y" | "N" | "NA" → sheet symbol.
function markSymbol(v) {
    return v === "Y" ? "✓" : v === "N" ? "✗" : v === "NA" ? "N/A" : "";
}
const META_COLUMNS = [
    { header: "Date", get: (c) => c.date ?? "" },
    { header: "Hospital", get: (c) => c.hospitalName ?? "" },
    { header: "Ambulance No", get: (c) => c.ambulanceNo ?? "" },
    { header: "Shift", get: (c) => (c.shift === "D" ? "Day" : c.shift === "N" ? "Night" : "") },
    { header: "Type", get: (c) => c.type ?? "" },
    { header: "Level", get: (c) => c.level ?? "" },
    { header: "Mileage (KM)", get: (c) => c.mileage ?? "" },
    { header: "Submitted At", get: (c) => fmtDateTime(c.submittedAt) },
    { header: "Submitted By", get: (c) => c.submittedByName ?? "" },
    {
        header: "Outgoing Medic",
        get: (c) => c.handover?.outgoingName ?? c.outgoingMedicName ?? "",
    },
    {
        header: "Outgoing Medic ID",
        get: (c) => c.handover?.outgoingId ?? c.medicBadgeNo ?? "",
    },
    {
        header: "Incoming Medic",
        get: (c) => c.handover?.incomingName ?? c.incomingMedicName ?? "",
    },
    { header: "Incoming Medic ID", get: (c) => c.handover?.incomingId ?? "" },
    {
        header: "External Note",
        get: (c) => c.externalNote ?? c.bodyNote ?? "",
    },
    {
        header: "ACLS Seal Number",
        get: (c) => c.aclsSealNumber ?? c.medicationBag?.sealNo ?? "",
    },
    { header: "Photos Attached", get: (c) => String(c.imageCount ?? c.images?.length ?? 0) },
];
// The meta columns are shared by every revision; only the per-item columns
// differ, so both the current and the frozen layouts come from these two.
/** Ordered column headers for a checklist row, for one revision's sections. */
export function buildChecklistHeaders(sections) {
    const headers = META_COLUMNS.map((m) => m.header);
    for (const sec of sections) {
        for (const item of sec.items)
            headers.push(`${sec.label} - ${item[1]}`);
    }
    return headers;
}
/** Flatten one checklist into a row aligned to that revision's headers. */
export function flattenChecklistWith(sections, c) {
    const row = META_COLUMNS.map((m) => m.get(c));
    // Only the monthly (1st of month) checklist stores expiry dates in the
    // per-item column; on every other day it holds remarks. The pre-2026
    // revision collected expiry dates every day.
    const expiryDay = isV1Checklist(c) || isExpiryDay(c.date);
    for (const sec of sections) {
        const markRec = (c[sec.key] ?? {});
        const extraRec = (c[sec.extraKey] ?? {});
        for (const item of sec.items) {
            const mark = markSymbol(markRec[item[0]]);
            const extra = (extraRec[item[0]] ?? "").trim();
            const prefix = extraKindFor(sec.extra, expiryDay) === "expiry" ? "exp " : "";
            row.push(extra ? `${mark || "—"} · ${prefix}${extra}` : mark);
        }
    }
    return row;
}
/** Ordered column headers for the CURRENT revision. */
export const CHECKLIST_HEADERS = buildChecklistHeaders(NEW_CHECKLIST_SECTIONS);
/** Flatten one checklist against the CURRENT revision's columns. */
export function flattenChecklist(c) {
    return flattenChecklistWith(NEW_CHECKLIST_SECTIONS, c);
}
//# sourceMappingURL=checklistColumns.js.map